<script setup lang="ts">
import { secondsToMilliseconds } from 'date-fns';
import { debounce } from 'lodash-es';
import smoothscroll from 'smoothscroll-polyfill';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { isCosme } from '@/business_specification/20211126-cosme';
import { isPanasonic, shouldHidePriceForPanasonic } from '@/business_specification/20260303-panasonic-hide-zero-price';
import { formatPriceForPanasonic } from '@/business_specification/20260601-panasonic-price-display';
import { usePostMessageReceiver } from '@/composables/usePostMessageReceiver';
import { useProductStyle } from '@/composables/useProductStyle';
import { recordProductClicked } from '@/js/api';
import { $, openNewWindowAfterFastCheckout } from '@/js/browser';
import { tre } from '@/plugins/bugsnag';
import { SALABLE_STATUS, SKU_COLLAPSE_STYLE, SKU_STYLE_TYPE } from '@/store/constants';
import { buyerPubnubActualId } from '@/store/dynamicGlobalVariables';
import { requestAddToCartNotification } from '@/store/streamPostMessenger';
import { useTemplate } from '@/store/templateData';
import { transformCurrencyToSymbol } from '@/utils/transformer/currency';
import { withDigitGrouping } from '@/utils/transformer/string';

import Product from '../Product/Product.vue';

interface ModuleProductListFlattenProps {
  module: {
    components: {
      title: {
        text: string;
      };
      subtitle: {
        text: string;
      };
      name: {
        textSize: string;
        lineHeight: string;
        textColor: string;
      };
      skuStyle: {
        type: 'expand' | 'collapse';
        collapseStyle: 'square' | 'circle' | 'hidden';
      };
      originalPrice: {
        enabled: boolean;
        textChoice: number;
      };
      price: {
        enabled: boolean;
        textSize: string;
        lineHeight: string;
        textColor: string;
      };
      shopcart: {
        enabled: boolean;
      };
      button: {
        text: string;
        textSize: string;
        textColor: string;
        lineHeight: string;
        fillStyle: 'outline' | 'solid';
      };
    };
  };
}

defineOptions({
  name: 'ModuleProductListFlattenDisplay'
});

const SCROLL_OFFSET = 24;

smoothscroll.polyfill();

const props = withDefaults(defineProps<ModuleProductListFlattenProps>(), {
  module: () => ({
    components: {
      title: {
        text: '',
      },
      subtitle: {
        text: '',
      },
      name: {
        textSize: '',
        lineHeight: '',
        textColor: '',
      },
      skuStyle: {
        type: SKU_STYLE_TYPE.EXPAND as 'expand',
        collapseStyle: SKU_COLLAPSE_STYLE.SQUARE as 'square',
      },
      originalPrice: {
        enabled: true,
        textChoice: 0,
      },
      price: {
        enabled: true,
        textSize: '',
        lineHeight: '',
        textColor: '',
      },
      shopcart: {
        enabled: true,
      },
      button: {
        text: '',
        textSize: '',
        textColor: '',
        lineHeight: '',
        fillStyle: 'outline' as 'outline',
      },
    },
  }),
});

const { t } = useI18n();
const { handsupConfig, productList } = useTemplate();

const slug = computed(() => handsupConfig.value?.store?.slug);
const postId = computed(() => handsupConfig.value?.post?.id);
const currencySymbol = computed(() => {
  const currency = handsupConfig.value?.store?.currency;
  return transformCurrencyToSymbol(currency ?? '');
});

const components = computed(() => props.module.components);
const isShowOriginalPrice = computed(() => components.value.originalPrice.enabled);
const isShowPrice = computed(() => components.value.price.enabled);
const isShowShoppingCart = computed(() => components.value.shopcart.enabled);

const originalPriceDescription = computed(() => {
  const i = components.value.originalPrice.textChoice;
  // t('cms_setting.display.product.original_price.text.0')
  // t('cms_setting.display.product.original_price.text.1')
  // t('cms_setting.display.product.original_price.text.2')
  // t('cms_setting.display.product.original_price.text.3')
  // t('cms_setting.display.product.original_price.text.4')
  return t(`cms_setting.display.product.original_price.text.${i}`);
});

const toPriceDisplay = (price: number) => {
  if (isPanasonic()) {
    if (shouldHidePriceForPanasonic(price)) { return ''; }
    return formatPriceForPanasonic(withDigitGrouping(price));
  }
  return [currencySymbol.value, withDigitGrouping(price)].join(' ').trim();
};

const toProductButtonDisplay = ({ isBeforeSell = false, isSoldOut = false, isEndSell = false }) => {
  if (isBeforeSell) { return t('button.upcoming_sell'); }
  if (isEndSell) { return t('button.end_sell'); }
  if (isSoldOut) { return t('button.sold_out'); }

  if (isCosme()) { return '商品ページへ'; }

  return components.value.button.text || t('button.buy');
};

const postMessageReceiver = usePostMessageReceiver();
const iframeEl = ref<HTMLElement | null>(null);
postMessageReceiver.onStreamReady(() => {
  iframeEl.value = document.querySelector('#HANDSUP_PIP iframe');
});

// NOTE: debounce 1 秒，避免連續觸發 API，造成 Server 的壓力
const checkoutShoppingCart = debounce(
  async (product: any) => {
    // trigger add to cart notice
    if (iframeEl.value) {
      requestAddToCartNotification(product.id);
    }

    if (!postId.value) {
      // TODO: error handling
      return;
    }
    try {
      // 記錄有興趣的點擊事件
      recordProductClicked({
        postId: postId.value,
        productId: product.salable_id,
        skuId: product.id,
        storeSlug: slug.value ?? '',
        pnActualId: buyerPubnubActualId.value ?? '',
        source: 'cms',
      });
      // 紀錄購買的點擊事件
      (window as any)?.HUT?.trackProductClickEvent?.(product.id);
      // 導轉結帳頁面
      // NOTE: DO NOT USE AWAIT ABOVE
      openNewWindowAfterFastCheckout(postId.value, product.id);
    } catch (error) {
      tre(error);
      // TODO: error handling
    }
  },
  secondsToMilliseconds(1),
  { leading: true },
);

const promotingProductIds = ref<Array<string | number>>([]);
postMessageReceiver.onPromotingProductChanged((products: Array<{ id: string | number }> | undefined) => {
  promotingProductIds.value = products?.map((product: { id: string | number }) => (product.id)) ?? [];
});

const getProductImageURL = (productImages: Array<{ id: string | number; url: string }>, productCoverImageId: string | number) => {
  // NOTE: 圖片顯示邏輯(商品封面圖 → 商品圖第一張 → 空字串)
  // https://17media.atlassian.net/wiki/spaces/H/pages/996507649/23Q3+Making+product+images+a+required+field+-#Story-3%EF%BC%9A%E7%95%B6-Seller-%2F-Buyer-%E6%AA%A2%E8%A6%96%E5%95%86%E5%93%81%E6%99%82%EF%BC%8C%E5%95%86%E5%93%81%E5%B1%95%E5%94%AE%E7%9A%84%E5%9C%96%E7%89%87%E6%9C%83%E6%98%AF-seller-%E8%A8%AD%E5%AE%9A%E7%9A%84%E5%95%86%E5%93%81%E5%B0%81%E9%9D%A2%E5%9C%96
  if (productImages.length === 0) { return null; }

  const foundProductCoverImage = productImages.find((productImage: { id: string | number; url: string }) => productImage.id === productCoverImageId);
  return foundProductCoverImage?.url ?? productImages[0].url;
};

const normalizedProductSkus = computed(() =>
  productList.value.flatMap((product: any) => {
    return product.skus.map((variant: any) => {
      // TODO: 待討論事項
      // - isBeforeSell、isEndSell、isOnSell 是拿 Product 層級判斷
      // - isSoldOut 是拿 Sku 層級判斷
      // 這樣往下傳至 Component(Product) 是否會有點亂呢？
      const isBeforeSell = product.salable_status === SALABLE_STATUS.BEFORE_SELL;
      const isEndSell = product.salable_status === SALABLE_STATUS.END_SELL;
      const isSoldOut = variant.salable_quantity <= 0 || product.salable_status === SALABLE_STATUS.SOLD_OUT;
      const isOnSell = product.salable_status === SALABLE_STATUS.ON_SELL;
      return {
        ...variant,
        salable_id: product.id,
        name: product.name,
        // NOTE: 不顯示 sku 預設名字的 'Free'
        // 目前是拿 Localize 的 Product.DialogAddProduct.default_sku_name 字串，只是剛好目前語系都是 'Free'
        skuName: variant.name.toLowerCase() === 'free' ? '' : variant.name,
        priceDisplay: toPriceDisplay(variant.price),
        originPriceDisplay: variant.original_price && toPriceDisplay(variant.original_price),
        imageUrl: getProductImageURL(product.images, product.cover_image_id),
        isBeforeSell,
        isEndSell,
        isSoldOut,
        isOnSell,
        isPromoting: promotingProductIds.value.includes((product as any).id),
        buttonName: toProductButtonDisplay({ isBeforeSell, isSoldOut, isEndSell }),
        isShowPrice: isShowPrice.value && !shouldHidePriceForPanasonic(variant.price),
      };
    });
  }),
);

const needRender = computed(() => [
  normalizedProductSkus.value.length,
].every(Boolean));

const { productNameStyle, productPriceStyle, productButtonStyle } = useProductStyle(components);

postMessageReceiver.onPromotingProductClicked((product: { id?: string | number }) => {
  if (!product?.id) {
    return;
  }

  // 記錄有興趣的點擊事件（來源：promoting products）
  const clickedProduct = normalizedProductSkus.value.find((item) => String(item.salable_id) === String(product.id));
  if (clickedProduct && postId.value) {
    recordProductClicked({
      postId: postId.value,
      productId: clickedProduct.salable_id,
      skuId: clickedProduct.id,
      storeSlug: slug.value ?? '',
      pnActualId: buyerPubnubActualId.value ?? '',
      source: 'cms',
      entryPoint: 'promoting-products',
    });
  }

  const productEl = $(`.product_${product.id}`);
  if (!productEl) {
    return;
  }

  const position = productEl.offsetTop - SCROLL_OFFSET;
  window.scrollTo({ top: position, behavior: 'smooth' });
});
</script>

<template>
<div
  class="module-product-list-flatten-display"
  v-if="needRender"
  ref="productListEl"
>
  <Product
    v-for="product in normalizedProductSkus"
    :key="product.id"
    :class="`product_${product.salable_id}`"
    :product="product"
    :isShowOriginalPrice="isShowOriginalPrice"
    :isShowPrice="product.isShowPrice"
    :isShowShoppingCart="isShowShoppingCart"
    :productNameStyle="productNameStyle"
    :productPriceStyle="productPriceStyle"
    :productButtonStyle="productButtonStyle"
    :skuType="SKU_STYLE_TYPE.EXPAND"
    :originalPriceDescription="originalPriceDescription"
    :isShowPanasonicOnlineStoreLabel="isPanasonic()"
    @shoppingButtonClicked="checkoutShoppingCart"
  />
</div>
</template>
<style lang="scss" scoped>
$desktop-fixed-width: 1075px;

.module-product-list-flatten-display {
  @apply grid grid-cols-3;
}

.tablet .module-product-list-flatten-display {
  @apply grid grid-cols-3;
}

.laptop .module-product-list-flatten-display {
  @apply grid grid-cols-5;
}

.desktop .module-product-list-flatten-display {
  @apply grid grid-cols-5;
}
</style>
